import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Building2 } from 'lucide-react';
import { useBusinesses } from '../hooks/useBusinesses';
import { useCreateBusinessForm } from '../hooks/useCreateBusinessForm';
import {
  BusinessInfoSection,
  ContactInfoSection,
  ServicesSection,
  AvailabilitySection,
  InsuranceSection
} from './sections';

/**
 * CreateBusiness component provides a form interface for creating a new business profile.
 *
 * This component allows users to input business details such as name, owner, contact information,
 * address, business type, hourly rate, services offered, availability, and optional insurance information.
 * It handles form state, validation, and submission, and displays error messages if submission fails.
 *
 * Features:
 * - Collects business and owner information.
 * - Collects contact and address details.
 * - Allows selection of business type and hourly rate.
 * - Allows selection of multiple services offered.
 * - Allows setting weekly availability.
 * - Optionally collects insurance provider details.
 * - Handles form validation and error display.
 * - Submits data via `createBusiness` and navigates on success.
 *
 * @component
 * @returns {JSX.Element} The rendered CreateBusiness form component.
 */
const CreateBusiness = () => {
  const { createBusiness } = useBusinesses();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    formData,
    errors,
    handleInputChange,
    handleAddressChange,
    handleServiceToggle,
    handleAvailabilityChange,
    handlePhoneBlur,
    handlePostalCodeBlur,
    validate,
    prepareSubmissionData,
    clearPersistedData
  } = useCreateBusinessForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    // Validate form
    const isValid = validate();
    if (!isValid) {
      setSubmitError('Please fix the errors in the form before submitting.');
      // Focus on first error field
      const firstErrorField = document.querySelector('[aria-invalid="true"]');
      if (firstErrorField && firstErrorField instanceof HTMLElement) {
        firstErrorField.focus();
      }
      return;
    }

    setLoading(true);

    try {
      const businessData = prepareSubmissionData();
      await createBusiness(businessData);
      // Clear persisted form data on successful submission
      clearPersistedData();
      navigate('/businesses');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred. Failed to create business.';
      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Clear persisted data when canceling
    clearPersistedData();
    navigate('/businesses');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/businesses')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Go back to businesses list"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center space-x-3">
            <div className="h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
              <Building2 className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Add New Business</h1>
              <p className="text-gray-500">Create a new partner business profile</p>
            </div>
          </div>
        </div>
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert" aria-live="assertive">
          <p className="text-red-700">{submitError}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        <BusinessInfoSection
          formData={formData}
          errors={errors}
          onChange={handleInputChange}
        />

        <ContactInfoSection
          formData={formData}
          errors={errors}
          onChange={handleInputChange}
          onAddressChange={handleAddressChange}
          onPhoneBlur={handlePhoneBlur}
          onPostalCodeBlur={handlePostalCodeBlur}
        />

        <ServicesSection
          selectedServices={formData.servicesOffered}
          errors={errors}
          onToggle={handleServiceToggle}
        />

        <AvailabilitySection
          availability={formData.availability}
          errors={errors}
          onChange={handleAvailabilityChange}
        />

        <InsuranceSection
          provider={formData.insuranceProvider}
          policyNumber={formData.policyNumber}
          expirationDate={formData.expirationDate}
          errors={errors}
          onChange={(field, value) => handleInputChange(field as keyof typeof formData, value)}
        />

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4">
          <button
            type="button"
            onClick={handleCancel}
            className="bg-gray-500 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-4 w-4" />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="bg-indigo-600 text-white px-6 py-2 rounded-lg flex items-center space-x-2 hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            aria-busy={loading}
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Creating...' : 'Create Business'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

/**
 * `CreateBusiness` is a React component responsible for rendering the UI and logic
 * for creating a new business entity within the admin application.
 *
 * @component
 * @returns {JSX.Element} The rendered component for creating a business.
 */
export default CreateBusiness;
